import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { communityPosts, studyGroups, helpProviders, emotionOptions } from '@/mocks/community';
import PairingSection from './components/PairingSection';
import CommunityAuthModal from './components/CommunityAuthModal';
import AIMatchModal from './components/AIMatchModal';
import ScoreRewardPanel from './components/ScoreRewardPanel';
import DonationWall from './components/DonationWall';
import VillageLifeSection from './components/VillageLifeSection';
import FarmLeaderboard from './components/FarmLeaderboard';

interface CommunityUser {
  name: string;
  role: string;
  email: string;
}

const roleColors: Record<string, string> = {
  student: 'bg-blue-100 text-blue-700',
  teacher: 'bg-green-100 text-green-700',
  parent: 'bg-amber-100 text-amber-700',
};
const roleLabels: Record<string, string> = {
  student: '学生',
  teacher: '教师',
  parent: '家长',
};

const channels = [
  {
    icon: 'ri-user-heart-line',
    title: '学生互助交流',
    desc: '城市与农村学生跨越地域，互加好友，共同学习进步',
    link: '/community/student',
    color: 'bg-blue-600',
    count: '12.3万人',
  },
  {
    icon: 'ri-user-star-line',
    title: '师生互动答疑',
    desc: '名师在线答疑，找学长辅导，疑难问题迎刃而解',
    link: '/community/teacher',
    color: 'bg-green-600',
    count: '8000+教师',
  },
  {
    icon: 'ri-parent-line',
    title: '家长助力找伴',
    desc: '家长帮孩子找同年级学伴，让孩子在互助中共同成长',
    link: '/community/parent',
    color: 'bg-amber-600',
    count: '5万+家长',
  },
];

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const communitySchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/community`,
  url: `${SITE_URL}/community`,
  name: '互动交流社区 - 城乡学生互助 | 金榜旺财',
  description: '城乡学生互助交流社区，AI智能匹配学伴，师生互动答疑，家长助力找伴，爱心捐助公示，农村生活分享，积分奖励系统。',
  inLanguage: 'zh-CN',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: '互动交流社区', item: `${SITE_URL}/community` },
    ],
  },
};

export default function CommunityPage() {
  const [communityUser, setCommunityUser] = useState<CommunityUser | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showAIMatch, setShowAIMatch] = useState(false);
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'all' | 'help' | 'student' | 'teacher' | 'parent' | 'village'>('all');
  const [showPostModal, setShowPostModal] = useState(false);
  const [postContent, setPostContent] = useState('');
  const [selectedEmotion, setSelectedEmotion] = useState('');
  const [activeSection, setActiveSection] = useState<'community' | 'donation' | 'village'>('community');

  useEffect(() => {
    const id = 'schema-community';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(communitySchema);
    document.title = '互动交流社区 - 城乡学生互助 | 金榜旺财';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', '城乡学生互助交流社区，AI智能匹配学伴，师生互动答疑，家长助力找伴，爱心捐助公示，农村生活分享，积分奖励系统。');
    return () => { const s = document.getElementById(id); if (s) s.remove(); };
  }, []);

  const toggleLike = (id: string) => {
    setLikedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAuthSuccess = (user: CommunityUser) => {
    setCommunityUser(user);
    setShowAuthModal(false);
  };

  const handlePostClick = () => {
    if (!communityUser) { setShowAuthModal(true); return; }
    setShowPostModal(true);
  };

  const filtered = activeTab === 'all'
    ? communityPosts
    : activeTab === 'help'
    ? communityPosts.filter(p => p.isHelpRequest || p.isHelper)
    : activeTab === 'village'
    ? communityPosts.filter(p => p.isVillageShare)
    : communityPosts.filter(p => p.role === activeTab);

  return (
    <div className="min-h-screen bg-[#faf9f7]">
      <Navbar />

      {/* Hero Header */}
      <div className="relative pt-20 pb-16 bg-gradient-to-br from-green-700 to-green-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/781af25f3b1eb7ab086da4ef9380f4d7.jpg"
            alt="社区背景"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-green-800/60 to-green-900/80"></div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-1.5 mb-5">
            <i className="ri-community-line text-white/90 text-sm"></i>
            <span className="text-white/90 text-sm font-semibold">互动交流社区</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white mb-4">城乡学生互助，师生共同成长</h1>
          <p className="text-white/80 text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-8">
            在这里，城市与农村的学生相遇，教师与学生共同进步，爱心家长慷慨捐助，让每一个孩子都有机会追逐梦想
          </p>

          {/* Stats Row */}
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            {[
              { label: '注册用户', value: '12.8万+' },
              { label: '成功配对', value: '3,200对' },
              { label: '爱心捐款', value: '¥234万' },
              { label: '受益学生', value: '8,900+' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-2xl font-black text-white">{stat.value}</p>
                <p className="text-white/60 text-xs">{stat.label}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {communityUser ? (
              <div className="flex items-center gap-3 bg-white/20 rounded-full px-5 py-2.5">
                <div className="w-8 h-8 rounded-full bg-amber-400 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{communityUser.name[0]}</span>
                </div>
                <span className="text-white font-semibold">欢迎回来，{communityUser.name}！</span>
              </div>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                className="px-8 py-3 bg-white text-green-700 rounded-full font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-user-add-line mr-2"></i>注册/登录加入社区
              </button>
            )}
            <button
              onClick={() => setShowAIMatch(true)}
              className="px-8 py-3 bg-green-500 text-white rounded-full font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 hover:bg-green-400 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-robot-2-line mr-2"></i>AI智能匹配学伴
            </button>
            <Link
              to="/donate"
              className="px-8 py-3 bg-rose-500 text-white rounded-full font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 hover:bg-rose-400 transition-all cursor-pointer flex items-center whitespace-nowrap"
            >
              <i className="ri-heart-pulse-fill mr-2"></i>爱心捐助
            </Link>
          </div>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="sticky top-16 md:top-20 z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex gap-0 overflow-x-auto">
            {[
              { key: 'community', label: '社区动态', icon: 'ri-community-line' },
              { key: 'donation', label: '爱心公示墙', icon: 'ri-heart-line' },
              { key: 'village', label: '农村生活分享', icon: 'ri-landscape-line' },
            ].map((sec) => (
              <button
                key={sec.key}
                onClick={() => setActiveSection(sec.key as typeof activeSection)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                  activeSection === sec.key
                    ? 'border-green-600 text-green-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className={sec.icon}></i>
                {sec.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Community Section */}
      {activeSection === 'community' && (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
          {/* Top Feature Banners */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-3xl p-6 md:p-8 text-white flex flex-col justify-center relative overflow-hidden group">
              <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-20 group-hover:opacity-30 transition-opacity">
                <i className="ri-graduation-cap-fill absolute -right-10 -bottom-10 text-[200px]"></i>
              </div>
              <div className="relative z-10 w-2/3">
                <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-xs font-bold mb-4 tracking-wider">AI智能匹配</span>
                <h2 className="text-2xl md:text-3xl font-black mb-3">找到你的学习伙伴</h2>
                <p className="text-white/80 text-sm leading-relaxed mb-6">
                  上传你的学习需求，AI分析后为你精准匹配最合适的学伴、教师或家长，互换微信/邮箱，开始互助之旅。
                </p>
                <button
                  onClick={() => setShowAIMatch(true)}
                  className="bg-white text-blue-700 px-6 py-2.5 rounded-full text-sm font-bold shadow-sm hover:shadow-md transition-all cursor-pointer whitespace-nowrap"
                >
                  立即AI匹配 <i className="ri-arrow-right-line ml-1"></i>
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-r from-rose-500 to-pink-600 rounded-3xl p-6 md:p-8 text-white flex flex-col justify-center relative overflow-hidden group">
              <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-20 group-hover:opacity-30 transition-opacity">
                <i className="ri-gift-fill absolute -right-4 -bottom-10 text-[200px]"></i>
              </div>
              <div className="relative z-10 w-2/3">
                <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-xs font-bold mb-4 tracking-wider">爱心公益</span>
                <h2 className="text-2xl md:text-3xl font-black mb-3">捐款买文具送农村</h2>
                <p className="text-white/80 text-sm leading-relaxed mb-6">
                  富裕家长小额捐款，平台统一购买文具、书籍、衣物，直接邮寄给农村学生，每笔捐款公开透明。
                </p>
                <button
                  onClick={() => setActiveSection('donation')}
                  className="inline-flex bg-white text-rose-600 px-6 py-2.5 rounded-full text-sm font-bold shadow-sm hover:shadow-md transition-all cursor-pointer whitespace-nowrap"
                >
                  查看爱心公示墙 <i className="ri-heart-fill ml-1.5"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Channel Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
            {channels.map((ch) => (
              <Link
                key={ch.title}
                to={ch.link}
                className={`${ch.color} rounded-2xl p-6 text-white cursor-pointer hover:scale-[1.02] transition-all block`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                    <i className={`${ch.icon} text-xl`}></i>
                  </div>
                  <span className="text-white/70 text-xs bg-white/10 px-2.5 py-1 rounded-full">{ch.count}</span>
                </div>
                <h3 className="font-bold text-base mb-2">{ch.title}</h3>
                <p className="text-white/80 text-sm leading-relaxed">{ch.desc}</p>
                <div className="flex items-center gap-1 mt-4 text-white/70 text-sm">
                  <span>进入频道</span>
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Posts Area */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-5 flex-wrap gap-4">
                <h2 className="text-xl font-black text-gray-900">社区动态圈</h2>
                <div className="flex gap-1 bg-white rounded-full p-1 shadow-sm overflow-x-auto">
                  {[
                    { key: 'all', label: '全部' },
                    { key: 'help', label: '求助/辅导' },
                    { key: 'village', label: '农村分享' },
                    { key: 'student', label: '学生' },
                    { key: 'teacher', label: '教师' },
                    { key: 'parent', label: '家长' },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key as typeof activeTab)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        activeTab === tab.key ? 'bg-green-600 text-white' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Post Input Box */}
              <div
                className="bg-white rounded-2xl p-4 mb-6 shadow-sm border border-gray-100 flex gap-4 items-center cursor-text"
                onClick={handlePostClick}
              >
                <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center text-gray-500">
                  {communityUser ? (
                    <span className="text-white font-bold text-sm bg-green-500 w-full h-full rounded-full flex items-center justify-center">
                      {communityUser.name[0]}
                    </span>
                  ) : (
                    <i className="ri-user-fill"></i>
                  )}
                </div>
                <div className="flex-1 bg-gray-50 rounded-full px-4 py-2.5 text-gray-400 text-sm">
                  {communityUser ? `${communityUser.name}，今天心情如何？遇到什么困难了吗...` : '登录后发布动态/求助...'}
                </div>
                <button className="w-10 h-10 bg-green-50 text-green-600 rounded-full flex items-center justify-center hover:bg-green-100 transition-colors">
                  <i className="ri-image-add-line"></i>
                </button>
              </div>

              <div className="flex flex-col gap-4">
                {filtered.map((post) => (
                  <div
                    key={post.id}
                    className={`bg-white rounded-2xl p-6 shadow-sm border ${
                      post.isHelpRequest ? 'border-orange-200' :
                      post.isHelper ? 'border-green-200' :
                      (post as { isVillageShare?: boolean }).isVillageShare ? 'border-emerald-200' :
                      'border-gray-100'
                    }`}
                  >
                    {post.isHelpRequest && (
                      <div className="flex items-center gap-2 text-orange-600 text-xs font-bold mb-4 bg-orange-50 px-3 py-1.5 rounded-lg inline-flex">
                        <i className="ri-error-warning-fill"></i> 求助帖
                      </div>
                    )}
                    {post.isHelper && (
                      <div className="flex items-center gap-2 text-green-600 text-xs font-bold mb-4 bg-green-50 px-3 py-1.5 rounded-lg inline-flex">
                        <i className="ri-hand-heart-fill"></i> 爱心辅导 / 经验分享
                      </div>
                    )}
                    {(post as { isVillageShare?: boolean }).isVillageShare && (
                      <div className="flex items-center gap-2 text-emerald-600 text-xs font-bold mb-4 bg-emerald-50 px-3 py-1.5 rounded-lg inline-flex">
                        <i className="ri-landscape-line"></i> 农村生活分享
                      </div>
                    )}

                    <div className="flex items-start gap-3 mb-4">
                      <img src={post.avatar} alt={post.author} className="w-12 h-12 rounded-full object-cover object-top flex-shrink-0 border-2 border-gray-50" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-bold text-gray-900 text-sm">{post.author}</span>
                          {post.emotion && <span className="text-sm">{post.emotion}</span>}
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleColors[post.role]}`}>
                            {roleLabels[post.role]}
                          </span>
                          <span className="text-xs text-gray-400">{post.grade}</span>
                          {post.region && <span className="text-xs text-gray-400">· {post.region}</span>}
                        </div>
                        <p className="text-gray-400 text-xs">{post.time}</p>
                      </div>
                      <button
                        onClick={() => !communityUser && setShowAuthModal(true)}
                        className={`px-4 py-1.5 text-xs rounded-full transition-colors cursor-pointer whitespace-nowrap font-semibold ${
                          post.isHelpRequest ? 'bg-orange-600 text-white hover:bg-orange-700' :
                          post.isHelper ? 'bg-green-600 text-white hover:bg-green-700' :
                          'border border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {post.isHelpRequest ? '提供帮助' : post.isHelper ? '求带飞' : '+ 加好友'}
                      </button>
                    </div>

                    <p className="text-gray-800 text-sm leading-relaxed mb-4 whitespace-pre-wrap">{post.content}</p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags.map((tag) => (
                        <span key={tag} className="text-xs bg-gray-50 text-gray-600 px-2.5 py-1 rounded-full border border-gray-100">#{tag}</span>
                      ))}
                    </div>

                    <div className="flex items-center gap-6 text-gray-400 text-xs border-t border-gray-50 pt-4 mt-2">
                      <button
                        onClick={() => toggleLike(post.id)}
                        className={`flex items-center gap-1.5 cursor-pointer transition-colors font-medium ${likedIds.has(post.id) ? 'text-rose-500' : 'hover:text-rose-500'}`}
                      >
                        <i className={`text-base ${likedIds.has(post.id) ? 'ri-heart-fill' : 'ri-heart-line'}`}></i>
                        {post.likes + (likedIds.has(post.id) ? 1 : 0)}
                      </button>
                      <button className="flex items-center gap-1.5 cursor-pointer hover:text-blue-500 transition-colors font-medium">
                        <i className="ri-chat-3-line text-base"></i> {post.comments}
                      </button>
                      <button className="flex items-center gap-1.5 cursor-pointer hover:text-green-500 transition-colors font-medium">
                        <i className="ri-share-forward-line text-base"></i> 分享
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="flex flex-col gap-6">
              {/* Score & Reward Panel */}
              <ScoreRewardPanel currentUser={communityUser} onOpenAuth={() => setShowAuthModal(true)} />

              {/* Find Helper Widget */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-900">免费爱心辅导榜</h3>
                  <Link to="/community/teacher" className="text-xs text-blue-600 hover:underline cursor-pointer">查看全部</Link>
                </div>
                <div className="flex flex-col gap-4">
                  {helpProviders.map((helper) => (
                    <div key={helper.id} className="flex gap-3 group">
                      <img src={helper.avatar} alt={helper.name} className="w-10 h-10 rounded-full object-cover border border-gray-100" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-bold text-sm text-gray-900 group-hover:text-blue-600 transition-colors">{helper.name}</span>
                          <span className="text-xs font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">免费</span>
                        </div>
                        <p className="text-xs text-gray-500 truncate mb-1">{helper.school}</p>
                        <div className="flex gap-1 flex-wrap mb-2">
                          {helper.subjects.map(s => (
                            <span key={s} className="text-[10px] bg-gray-50 text-gray-600 px-1.5 py-0.5 rounded">{s}</span>
                          ))}
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2 text-gray-400">
                            <span className="text-amber-500"><i className="ri-star-fill"></i> {helper.rating}</span>
                            <span>帮助 {helper.helped} 人</span>
                          </div>
                          <button
                            onClick={() => !communityUser && setShowAuthModal(true)}
                            className="text-blue-600 font-medium hover:underline cursor-pointer"
                          >
                            联系TA
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Farm Leaderboard */}
              <FarmLeaderboard />

              {/* Study Groups */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-4">热门学习群组</h3>
                <div className="flex flex-col gap-4">
                  {studyGroups.map((group) => (
                    <div key={group.id} className="flex items-center gap-3 cursor-pointer group">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-xl flex-shrink-0 group-hover:bg-green-50 transition-colors">
                        <i className={`${group.icon} text-gray-600 text-lg group-hover:text-green-600 transition-colors`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-800 group-hover:text-green-600 transition-colors truncate">{group.name}</p>
                        <p className="text-xs text-gray-400">{group.members.toLocaleString()}人</p>
                      </div>
                      <button
                        onClick={() => !communityUser && setShowAuthModal(true)}
                        className="text-xs text-green-600 font-semibold bg-green-50 px-3 py-1.5 rounded-full hover:bg-green-600 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                      >
                        加入
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Pairing Section */}
          <div className="mt-10">
            <PairingSection />
          </div>
        </div>
      )}

      {/* Donation Wall Section */}
      {activeSection === 'donation' && <DonationWall />}

      {/* Village Life Section */}
      {activeSection === 'village' && <VillageLifeSection />}

      <Footer />

      {/* Auth Modal */}
      {showAuthModal && (
        <CommunityAuthModal
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
      )}

      {/* AI Match Modal */}
      {showAIMatch && (
        <AIMatchModal
          onClose={() => setShowAIMatch(false)}
          currentUser={communityUser}
          onOpenAuth={() => { setShowAIMatch(false); setShowAuthModal(true); }}
        />
      )}

      {/* Post Modal */}
      {showPostModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-lg text-gray-900">发布动态/求助</h3>
              <button onClick={() => setShowPostModal(false)} className="text-gray-400 hover:text-gray-700 transition-colors cursor-pointer">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <label className="text-xs font-bold text-gray-500 mb-2 block uppercase tracking-wider">记录当前心情/状态</label>
                <div className="flex flex-wrap gap-2">
                  {emotionOptions.map((emo) => (
                    <button
                      key={emo.label}
                      onClick={() => setSelectedEmotion(emo.emoji)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all cursor-pointer border ${
                        selectedEmotion === emo.emoji
                          ? 'border-green-500 bg-green-50 text-green-700 font-bold shadow-sm'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-base">{emo.emoji}</span> {emo.label}
                    </button>
                  ))}
                </div>
              </div>

              <textarea
                value={postContent}
                onChange={(e) => setPostContent(e.target.value)}
                placeholder="分享你的学习心得，或者写下你需要什么帮助（找题解、找人讲课等）..."
                className="w-full h-32 bg-gray-50 border border-gray-100 rounded-2xl p-4 text-sm text-gray-800 outline-none focus:border-green-400 focus:bg-white transition-all resize-none mb-4"
                maxLength={500}
              ></textarea>

              <div className="flex gap-2 mb-6">
                <button className="flex items-center gap-1 px-3 py-1.5 bg-gray-50 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 cursor-pointer">
                  <i className="ri-image-add-line"></i> 添加图片
                </button>
                <button className="flex items-center gap-1 px-3 py-1.5 bg-orange-50 rounded-lg text-xs font-medium text-orange-600 hover:bg-orange-100 cursor-pointer">
                  <i className="ri-error-warning-line"></i> 标记为求助帖
                </button>
                <button className="flex items-center gap-1 px-3 py-1.5 bg-emerald-50 rounded-lg text-xs font-medium text-emerald-600 hover:bg-emerald-100 cursor-pointer">
                  <i className="ri-landscape-line"></i> 农村生活分享
                </button>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowPostModal(false)}
                  className="px-6 py-2.5 rounded-full text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  取消
                </button>
                <button
                  onClick={() => {
                    setShowPostModal(false);
                    setPostContent('');
                    setSelectedEmotion('');
                  }}
                  className="px-8 py-2.5 bg-green-600 text-white rounded-full text-sm font-bold shadow-md hover:bg-green-700 hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  发布
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
